import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { CreditCard, Shield, Lock, CheckCircle, X, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount || 0);
};

const DeclinedPaymentForm = () => {
  const { linkId } = useParams();
  
  // Link validation state
  const [linkData, setLinkData] = useState(null);
  const [isValidLink, setIsValidLink] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [linkError, setLinkError] = useState('');
  
  // Payment state
  const [savedCards, setSavedCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [showNewCardForm, setShowNewCardForm] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // New card data
  const [newCardData, setNewCardData] = useState({
    cardNumber: '',
    cardName: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    address: '',
    city: '',
    state: '',
    zip: '',
  });
  
  // Success/Error states
  const [showSuccess, setShowSuccess] = useState(false);
  const [showAlreadyPaid, setShowAlreadyPaid] = useState(false);
  const [transactionResult, setTransactionResult] = useState(null);
  const [paymentError, setPaymentError] = useState('');
  const [alreadyPaidData, setAlreadyPaidData] = useState(null);

  // Load and validate the payment link
  useEffect(() => {
    const validateLink = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`/api/payments/declined-link/${linkId}`);
        
        if (response.data.success) {
          setLinkData(response.data.linkData);
          setSavedCards(response.data.savedCards || []);
          setIsValidLink(true);
        } else if (response.data.alreadyPaid) {
          // Payment already completed - show success page
          setAlreadyPaidData({
            clientName: response.data.linkData.clientName,
            amount: response.data.linkData.amount,
            agentName: response.data.linkData.agentName,
            completedAt: response.data.completedAt,
            completedTransaction: response.data.completedTransaction
          });
          setShowAlreadyPaid(true);
          setIsValidLink(false);
        } else {
          setLinkError(response.data.message || 'Invalid payment link');
          setIsValidLink(false);
        }
      } catch (error) {
        console.error('Error validating link:', error);
        setLinkError(error.response?.data?.message || 'Failed to load payment link');
        setIsValidLink(false);
      } finally {
        setIsLoading(false);
      }
    };

    if (linkId) {
      validateLink();
    }
  }, [linkId]);

  // Handle card selection
  const handleCardSelection = useCallback((card) => {
    setSelectedCard(card);
    setShowNewCardForm(false);
  }, []);

  // Handle new card toggle
  const handleNewCardToggle = useCallback(() => {
    setShowNewCardForm(true);
    setSelectedCard(null);
  }, []);

  // Process payment
  const handlePayment = useCallback(async () => {
    if (!selectedCard && !showNewCardForm) {
      setPaymentError('Please select a payment method');
      return;
    }

    if (showNewCardForm) {
      // Validate new card data
      if (!newCardData.cardNumber || !newCardData.cardName || !newCardData.expiryMonth || 
          !newCardData.expiryYear || !newCardData.cvv) {
        setPaymentError('Please fill in all required card information');
        return;
      }
    }

    try {
      setIsProcessing(true);
      setPaymentError('');
      
      const paymentData = {
        linkId,
        paymentMethod: showNewCardForm ? 'new_card' : 'saved_card',
        cardId: selectedCard?.cardId,
        newCardData: showNewCardForm ? newCardData : undefined
      };

      const response = await axios.post('/api/payments/process-declined-payment', paymentData);
      
      if (response.data.success) {
        setTransactionResult(response.data);
        setShowSuccess(true);
      } else {
        setPaymentError(response.data.message || 'Payment failed');
      }
    } catch (error) {
      console.error('Payment error:', error);
      
      let errorMessage = 'Payment processing failed';
      const errorData = error.response?.data;
      
      if (errorData?.message) {
        errorMessage = errorData.message;
      } else if (errorData?.error) {
        errorMessage = errorData.error;
      }
      
      // Common decline reasons
      if (errorMessage.toLowerCase().includes('declined')) {
        errorMessage += '\n\nCommon reasons for declines:\n• Insufficient funds\n• Card expired or blocked\n• Incorrect card information\n• Daily limit exceeded';
      } else if (errorMessage.toLowerCase().includes('invalid card')) {
        errorMessage = 'Invalid card information. Please check your card number, expiration date, and CVV.';
      } else if (errorMessage.toLowerCase().includes('expired')) {
        errorMessage = 'Your card has expired. Please use a different payment method.';
      }
      
      setPaymentError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  }, [linkId, selectedCard, showNewCardForm, newCardData]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-lg text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading payment information...</p>
        </div>
      </div>
    );
  }

  // Invalid link state
  if (!isValidLink) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-lg text-center max-w-md">
          <AlertTriangle size={48} className="text-red-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-4">Invalid Payment Link</h2>
          <p className="text-gray-600 mb-4">{linkError}</p>
          <p className="text-sm text-gray-500 mb-4">
            This link may have expired or been used already. Please contact your agent for assistance.
          </p>
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <div className="paylow-logo text-lg">
              <span className="pay">Pay</span><span className="low">Low</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Insurance Payment Portal
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Already paid state
  if (showAlreadyPaid) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-lg text-center max-w-md">
          <CheckCircle size={48} className="text-green-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-4">Payment Already Completed!</h2>
          <p className="text-gray-600 mb-4">
            Your payment of {formatCurrency(alreadyPaidData?.amount)} was successfully processed.
          </p>
          <div className="bg-gray-50 p-4 rounded-lg text-left space-y-2">
            <div>
              <p className="text-sm text-gray-600">Completed:</p>
              <p className="text-sm font-medium">{new Date(alreadyPaidData?.completedAt).toLocaleString()}</p>
            </div>
            {alreadyPaidData?.completedTransaction && (
              <div>
                <p className="text-sm text-gray-600">Transaction ID:</p>
                <p className="font-mono text-xs">{alreadyPaidData.completedTransaction.transactionId}</p>
              </div>
            )}
          </div>
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Agent:</strong> {alreadyPaidData?.agentName}
            </p>
            <div className="mt-3 text-center">
              <div className="paylow-logo text-lg">
                <span className="pay">Pay</span><span className="low">Low</span>
              </div>
              <p className="text-xs text-blue-600 mt-1">
                Insurance Payment Portal • Secure & Encrypted
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (showSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-lg text-center max-w-md">
          <CheckCircle size={48} className="text-green-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-4">Payment Successful!</h2>
          <p className="text-gray-600 mb-4">
            Your payment of {formatCurrency(linkData?.amount)} has been processed successfully.
          </p>
          <div className="bg-gray-50 p-4 rounded-lg text-left">
            <p className="text-sm text-gray-600">Transaction ID:</p>
            <p className="font-mono text-sm">{transactionResult?.transactionId}</p>
          </div>
          <p className="text-sm text-gray-500 mt-4">
            You should receive a confirmation email shortly.
          </p>
        </div>
      </div>
    );
  }

  // Main payment form
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Shield size={32} className="text-blue-600 mr-3" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Secure Payment Portal</h1>
                <p className="text-gray-600">Complete your payment securely</p>
              </div>
            </div>
            <div className="text-right">
              <div className="paylow-logo text-2xl">
                <span className="pay">Pay</span><span className="low">Low</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Insurance Portal</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Payment Summary */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Payment Summary</h2>
            
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-600">Client:</span>
                <span className="font-medium">{linkData.clientName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Payment Type:</span>
                <span className="font-medium">{linkData.originalTransaction.paymentType}</span>
              </div>
              {linkData.originalTransaction.policyNumber && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Policy Number:</span>
                  <span className="font-medium">{linkData.originalTransaction.policyNumber}</span>
                </div>
              )}
              <div className="border-t pt-4 mt-4">
                <div className="flex justify-between text-lg font-bold">
                  <span className="text-gray-900">Amount Due:</span>
                  <span className="text-blue-600">{formatCurrency(linkData.amount)}</span>
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center">
                <Lock size={16} className="text-blue-600 mr-2" />
                <span className="text-sm text-blue-800">
                  This is a secure payment portal. Your information is encrypted and protected.
                </span>
              </div>
            </div>
          </div>

          {/* Payment Method Selection */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Payment Method</h2>

            {/* Saved Cards */}
            {savedCards.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Saved Payment Methods</h3>
                <div className="space-y-2">
                  {savedCards.map((card) => (
                    <div
                      key={card.cardId}
                      onClick={() => handleCardSelection(card)}
                      className={`p-4 border rounded-lg cursor-pointer transition-all ${
                        selectedCard?.cardId === card.cardId 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <CreditCard size={20} className="text-gray-600 mr-3" />
                          <div>
                            <div className="font-medium">
                              {card.displayData?.cardType} •••• {card.displayData?.last4}
                            </div>
                            <div className="text-sm text-gray-500">
                              Expires {card.displayData?.expiryMonth}/{card.displayData?.expiryYear}
                            </div>
                          </div>
                        </div>
                        {selectedCard?.cardId === card.cardId && (
                          <CheckCircle size={20} className="text-blue-600" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* New Card Option */}
            <div className="mb-6">
              <button
                onClick={handleNewCardToggle}
                className={`w-full p-4 border rounded-lg text-left transition-all ${
                  showNewCardForm 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <CreditCard size={20} className="text-gray-600 mr-3" />
                    <span className="font-medium">Use a different card</span>
                  </div>
                  {showNewCardForm && (
                    <CheckCircle size={20} className="text-blue-600" />
                  )}
                </div>
              </button>
            </div>

            {/* New Card Form */}
            {showNewCardForm && (
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900">Card Information</h4>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Card Number</label>
                  <input
                    type="text"
                    value={newCardData.cardNumber}
                    onChange={(e) => setNewCardData({...newCardData, cardNumber: e.target.value.replace(/\D/g, '')})}
                    placeholder="1234 5678 9012 3456"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    maxLength={16}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cardholder Name</label>
                  <input
                    type="text"
                    value={newCardData.cardName}
                    onChange={(e) => setNewCardData({...newCardData, cardName: e.target.value})}
                    placeholder="John Smith"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                    <select
                      value={newCardData.expiryMonth}
                      onChange={(e) => setNewCardData({...newCardData, expiryMonth: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">MM</option>
                      {Array.from({length: 12}, (_, i) => (
                        <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                          {String(i + 1).padStart(2, '0')}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                    <select
                      value={newCardData.expiryYear}
                      onChange={(e) => setNewCardData({...newCardData, expiryYear: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">YYYY</option>
                      {Array.from({length: 10}, (_, i) => {
                        const year = new Date().getFullYear() + i;
                        return (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">CVV</label>
                    <input
                      type="text"
                      value={newCardData.cvv}
                      onChange={(e) => setNewCardData({...newCardData, cvv: e.target.value.replace(/\D/g, '')})}
                      placeholder="123"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      maxLength={4}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Error Display */}
            {paymentError && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <X size={16} className="text-red-600 mr-2" />
                  <span className="text-red-800">{paymentError}</span>
                </div>
              </div>
            )}

            {/* Pay Button */}
            <button
              onClick={handlePayment}
              disabled={isProcessing || (!selectedCard && !showNewCardForm)}
              className={`w-full py-4 px-6 rounded-lg font-semibold transition-all ${
                isProcessing || (!selectedCard && !showNewCardForm)
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {isProcessing ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Processing Payment...
                </div>
              ) : (
                `Pay ${formatCurrency(linkData.amount)}`
              )}
            </button>

            <div className="mt-4 flex items-center justify-center">
              <Lock size={16} className="text-gray-400 mr-2" />
              <span className="text-sm text-gray-500">
                Secured with 256-bit SSL encryption
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeclinedPaymentForm;